import type { PropsWithChildren, ReactNode } from 'react'
import React, { useEffect, useRef, useState } from 'react'
import { Popover } from '@systemeio/components'
import {
  Bold01Icon,
  Italic01Icon,
  Link01Icon,
  ListIcon,
  NumberedListBulletsIcon,
  Save01Icon,
  LinkBroken02Icon,
} from '@systemeio/icons'
import { Editor, Range } from 'slate'
import { ReactEditor, useSlate } from 'slate-react'
import { twJoin } from 'tailwind-merge'
import { isMarkActive, toggleBlock, toggleMark } from 'modules/community/hooks/slate-editor-utils'
import { isBlockActive } from 'modules/community/utils/editor/core'
import { insertLink, unwrapLink } from 'modules/community/utils/editor/link'

type AnchorElement = {
  getBoundingClientRect: () => DOMRect
}

const computeAnchorEl = (): AnchorElement => {
  const native = globalThis.getSelection()
  const range = native!.getRangeAt(0)
  return {
    getBoundingClientRect: () => range.getBoundingClientRect(),
  }
}

function SlateBlockButton({
  format,
  label,
  children,
}: PropsWithChildren<{
  format: string
  label: string
}>): ReactNode {
  const editor = useSlate()
  const active = isBlockActive(editor, format)
  return (
    <button
      className={twJoin(
        'relative z-10 inline-flex items-center px-2 py-1 [&_svg]:fill-current',
        active ? 'text-text-primary' : 'text-text-secondary',
      )}
      onMouseDown={event => {
        event.preventDefault()
        toggleBlock(editor, format)
      }}
      title={label}
      type="button"
    >
      {children}
    </button>
  )
}

function SlateMarkButton({
  format,
  label,
  children,
}: PropsWithChildren<{ format: string; label: string }>): ReactNode {
  const editor = useSlate()
  const active = isMarkActive(editor, format)
  return (
    <button
      className={twJoin(
        'relative z-10 inline-flex items-center px-2 py-1 [&_svg]:fill-current',
        active ? 'text-text-primary' : 'text-text-secondary',
      )}
      onMouseDown={event => {
        event.preventDefault()
        toggleMark(editor, format)
      }}
      title={label}
      type="button"
    >
      {children}
    </button>
  )
}

function SlateLinkButton({
  format,
  label,
  setLinkIsEditing,
}: {
  format: string
  label: string
  setLinkIsEditing: (isEditing: boolean) => void
}): ReactNode {
  const editor = useSlate()
  const active = isBlockActive(editor, format)

  return (
    <button
      className={twJoin(
        'relative z-10 inline-flex items-center px-2 py-1 [&_svg]:fill-current',
        active ? 'text-text-primary' : 'text-text-secondary hover:text-text-primary',
      )}
      onMouseDown={event => {
        event.preventDefault()
        if (active) {
          unwrapLink(editor)
        } else {
          setLinkIsEditing(true)
        }
      }}
      title={label}
      type="button"
    >
      {active ? <LinkBroken02Icon height="15px" /> : <Link01Icon height="15px" />}
    </button>
  )
}

export default function SlateHoveringToolbar(): ReactNode {
  const [open, setOpen] = useState(false)
  const editor = useSlate()
  const { selection } = editor
  const [anchorElement, setAnchorElement] = useState<AnchorElement | null>(null)
  const [linkIsEditing, setLinkIsEditing] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (
      !selection ||
      !ReactEditor.isFocused(editor) ||
      Range.isCollapsed(selection) ||
      Editor.string(editor, selection) === ''
    ) {
      setOpen(false)
      return
    }
    setAnchorElement(computeAnchorEl())
    setOpen(true)
  }, [editor, selection])

  const handleSaveLink = (): void => {
    if (inputRef.current) {
      const url = inputRef.current.value
      if (url) insertLink(editor, url)
      setLinkIsEditing(false)
    }
  }

  const closeOnEnterKey = (event: React.KeyboardEvent<HTMLInputElement>): void => {
    if (event.key === 'Enter') {
      handleSaveLink()
    }
  }

  return (
    <Popover.Root open={open}>
      <Popover.Portal>
        <Popover.Positioner anchor={anchorElement} side="bottom" sideOffset={12}>
          <Popover.Popup className="z-10">
            {!linkIsEditing && (
              <>
                <SlateMarkButton format="bold" label="bold">
                  <Bold01Icon height="15px" />
                </SlateMarkButton>
                <SlateMarkButton format="italic" label="italic">
                  <Italic01Icon height="15px" />
                </SlateMarkButton>
                <SlateBlockButton format="bulleted-list" label="bulleted-list">
                  <ListIcon height="15px" />
                </SlateBlockButton>
                <SlateBlockButton format="numbered-list" label="numbered-list">
                  <NumberedListBulletsIcon height="15px" />
                </SlateBlockButton>
                <SlateLinkButton setLinkIsEditing={setLinkIsEditing} format="link" label="link" />
              </>
            )}
            {linkIsEditing && (
              <div className="flex">
                <input className="block w-32 border-2" ref={inputRef} onKeyDown={closeOnEnterKey} />
                <Save01Icon
                  className="ml-1 cursor-pointer self-center fill-text-secondary"
                  height="20px"
                  onClick={handleSaveLink}
                />
              </div>
            )}
          </Popover.Popup>
        </Popover.Positioner>
      </Popover.Portal>
    </Popover.Root>
  )
}
